"use client";

import { motion, useReducedMotion } from "framer-motion";
import { useTranslations } from "next-intl";

import Badge2026 from "@/components/sections/2026/ui/Badge2026";
import { ShinyButton } from "@/components/ui/shiny-button";
import ResponsiveCopy from "@/components/sections/2026/ui/ResponsiveCopy";

// Centered "Our Story" content that sits over the StarsBackground. Each block
// rises + fades in on view with a small stagger (same pattern as the Portfolio
// header / Hero). The blue headline words use a static blue gradient (no cursor
// spotlight) so the heading stays calm.
//
// The hidden state is the same whether or not the visitor asks for reduced
// motion — it is what the server writes into the HTML, and the server cannot
// know that preference, so branching it is a hydration mismatch. Reduced motion
// lives in the transition: the travel gets zero duration, the fade still plays.
const HIDDEN = { opacity: 0, y: 24 };

const REDUCED = { opacity: { duration: 0.3 }, y: { duration: 0 } };

// Static blue gradient applied to the highlighted headline words.
const GRADIENT_WORD =
  "bg-[linear-gradient(100deg,#8fbaff,#4f93f2)] bg-clip-text text-transparent";

// Shared by both halves of the headline's responsive pair.
const headlineTags = {
  grad: (chunks) => <span className={GRADIENT_WORD}>{chunks}</span>,
  br: () => <br />,
};

export default function AboutUsContent() {
  const t = useTranslations("home.aboutUs");
  const reduce = useReducedMotion();
  const variants = {
    hidden: HIDDEN,
    show: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: reduce
        ? REDUCED
        : { duration: 0.5, delay: i * 0.12, ease: "easeOut" },
    }),
  };
  const reveal = {
    initial: "hidden",
    whileInView: "show",
    viewport: { once: true, amount: 0.4 },
    variants,
  };

  return (
    <div className="relative z-10 flex w-full max-w-[1360px] flex-col items-center gap-6 text-center">
      <motion.div {...reveal} custom={0}>
        <Badge2026>{t("badge")}</Badge2026>
      </motion.div>

      <motion.h2
        {...reveal}
        custom={1}
        className="text-4xl font-bold leading-tight text-white md:text-6xl"
      >
        <ResponsiveCopy
          desktop={t.rich("headline", headlineTags)}
          mobile={t.rich("headlineMobile", headlineTags)}
        />
      </motion.h2>

      <motion.p
        {...reveal}
        custom={2}
        className="max-w-[698px] text-base font-normal text-white"
      >
        <ResponsiveCopy desktop={t("body")} mobile={t("bodyMobile")} />
      </motion.p>

      <motion.div {...reveal} custom={3}>
        <ShinyButton href="/about-us">{t("learnMore")}</ShinyButton>
      </motion.div>
    </div>
  );
}
